<template>
  <div class="rating">
    <div class="rating-container">
      <div>
        <h1 class="title">{{ t('nps_title') }}</h1>
        <p class="transcript-email" v-if="email" v-html="t('transcript_messages_done', { email: email })"/>
        <p class="intro">{{ t('nps_intro')}}</p>
        <div class="slider-container" :class="{'slider-container-transcript': email}">
          <div class="slider-content">
            <img v-if="initialState" :src="this.sliderSwipeInstruction" alt="">
            <p v-if="initialState" class="slide-message">{{ t('nps_slideMessage') }}</p>
            <span v-else> {{ sliderValue }} </span>
          </div>
          <input
            v-model="sliderValue"
            type="range"
            min="0"
            max="10"
            class="slider"
            @input="initialState = false"
            @change="initialState = false"
          >
          <div class="rating-description">
            <p> {{ t('nps_negativeRating') }} </p>
            <p> {{ t('nps_positiveRating') }} </p>
          </div>
        </div>
      </div>
      <div class="buttons-container" :class="$mqAliases.isMobile ? 'mobile-buttons' : ''">
        <ChatButton
          class="sanitas-button send"
          :types="['primary']"
          :label="t('nps_buttons_send')"
          :disabled="initialState || ratingChosen"
          @click="sendRating()"
        />
        <ChatButton
          class="sanitas-button skip"
          :label="t('nps_buttons_skip')"
          :disabled="ratingChosen"
          @click="skipRating()"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { useI36n } from '@jota-one/i36n/src/index.vue2'
import ChatButton from './ChatButton'

export default {
  name: 'NpsRating',
  components: {
    ChatButton
  },

  setup () {
    const { $label: t } = useI36n()

    return { t }
  },

  data () {
    return {
      sliderValue: 5,
      initialState: true,
      ratingChosen: false
    }
  },
  computed: {
    ...mapGetters('uistate', ['sliderSwipeInstruction']),
    email: {
      get () {
        return this.$store.state.uistate.transcriptEmail
      },
      set (value) {
        this.$store.commit('uistate/SET_TRANSCRIPT_EMAIL', value)
      }
    }
  },
  methods: {
    sendRating () {
      this.ratingChosen = true
      this.$store.dispatch('conversation/sendNpsRating', { value: this.sliderValue })
      this.email = ''
    },
    skipRating () {
      this.ratingChosen = true
      this.$store.dispatch('conversation/conversationClose')
      this.email = ''
    }
  }
}
</script>

<style scoped lang="postcss">
.rating {
  overflow: auto;
  height: 100%;
  box-shadow: 0 6px 6px -3px rgba(0,0,0,0.2), 0 10px 14px 1px rgba(0,0,0,0.14), 0 4px 18px 3px rgba(0,0,0,0.12);
  padding: 15px;
  background-color: white;

  &-container {
    display: flex;
    flex-wrap: wrap;
    flex-direction: row;
    height: 100%;

    .title {
      word-break: break-all;
      margin: 20px 15px 20px 15px;
      text-align: center;
      background-color: #fff;
      font-family: 'Open Sans', sans-serif;
      font-size: 25px;
      font-weight: bold;
      line-height: 1.2;
      color: #191919;
    }
    .intro {
      text-align: left;
      padding: 0 40px;
      font-family: 'Open Sans', sans-serif;
      font-size: 15px;
      line-height: 1.33;
      color: #191919;
    }
    .transcript-email {
      font-size: 15px;
      color: #191919;
      word-break: break-word;
      padding: 0 40px 5px;
    }
  }
  .header-exists & {
    .rating-container {
      padding-top: 39px;
    }
  }
  .slider-container {
    background-color: white;
    height: auto;
    margin: 30px 15px 30px;
    text-align: center;

    .slider-content {
      display: block;
      height: 190px;
      font-size: 120px;
      font-weight: bold;
      line-height: 100px;
      align-items: center;
      justify-content: center;

      span {
        height: 200px;
        display: inline-flex;
        align-items: center;
      }
      .slide-message {
        margin: 14px 0 55px;
        font-family: 'Open Sans', sans-serif;
        font-size: 25px;
        font-weight: normal;
        font-stretch: normal;
        font-style: normal;
        line-height: 1.2;
        letter-spacing: normal;
        text-align: center;
        color: #191919;
      }
    }
    .slider {
      -webkit-appearance: none;
      width: 100%;
      height: 8px;
      background: #d3d3d3;
      outline: none;
      opacity: 0.7;
      -webkit-transition: .2s;
      transition: opacity .2s;
      cursor: pointer;

      @media all and (-ms-high-contrast:none)
      {
        background: transparent;
        height: 50px;
        box-sizing: content-box;
      }
      @supports (-ms-ime-align:auto) {
        background: transparent;
        height: 50px;
      }
    }
    .slider::-ms-track {
      width: 100%;
      height: 40px;
      cursor: pointer;
      /* Hides the slider so custom styles can be added */
      background: transparent;
      border-color: white;
      border-width: 20px 0;
      color: transparent;
    }
    .slider::-ms-fill-lower {
      background: #bbb;
      border-radius: 0;
    }
    .slider::-ms-fill-upper {
      background: #bbb;
      border-radius: 0;
    }
    .slider::-ms-thumb {
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background-image: url('../assets/icons/circle-carets.svg');
      cursor: pointer;
      background-color: white;
      border: none;
    }
    .slider::-webkit-slider-thumb {
      -webkit-appearance: none;
      appearance: none;
      width: 40px;
      height: 40px;
      background-image: url('../assets/icons/circle-carets.svg');
      background-color: white;
      cursor: pointer;
    }
    .slider::-moz-range-thumb {
      width: 40px;
      height: 40px;
      cursor: pointer;
      background-image: url('../assets/icons/circle-carets.svg');
      background-color: white;
    }
    .slider-caret {
      position: absolute;
      top: -30px;
    }
    .left {
      margin-left: -20px;
      transform: rotate(180deg)
    }
    .rating-description {
      display: flex;
      justify-content: space-between;
      margin-top: 15px;
      p {
        font-size: 12px;
        color: #191919;
      }
    }
  }
  .slider-container-transcript {
    margin: 0 40px 0;
  }
  .buttons-container {
    align-self: end;
    padding: 0 15px;
    width: 100%;

    .sanitas-button {
      border-radius: 25px;
      height: 50px;
      width: 100%;
    }
    .skip {
      background-color: #fff;
      color: #4CAF50;
      text-decoration: none;
      border: 1px solid #4CAF50;
    }
    .disabled {
      background-color: #d3d3d3 !important;
    }
  }
  .mobile-buttons {
    width: 100%;
    margin-top: auto;
  }
}
</style>
